
// ABOUTME: Hook for managing subscription data and Stripe integration
// ABOUTME: Provides subscription status, plans, and checkout functionality

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { logger } from '@/utils/sentry';

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  features: string[];
  max_users: number;
  max_referrals: number;
  is_active: boolean;
}

export interface SubscriptionStatus {
  hasSubscription: boolean;
  subscriptions: any[];
  customer_id?: string;
}

export function useSubscriptions() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price_monthly', { ascending: true });

      if (error) {
        logger.error("Failed to fetch subscription plans", { error: error.message });
        throw error;
      }

      return data as SubscriptionPlan[];
    },
  });

  const { data: subscriptionStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['subscription-status', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        logger.error("Failed to check subscription status", { error: error.message });
        throw error;
      }

      return data as SubscriptionStatus;
    },
    enabled: !!user,
  });

  const createCheckoutMutation = useMutation({
    mutationFn: async ({ planId, billingCycle = 'monthly' }: { planId: string; billingCycle?: 'monthly' | 'yearly' }) => {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { planId, billingCycle }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data.url) {
        window.open(data.url, '_blank');
      }
    },
    onError: (error) => {
      logger.error("Failed to create checkout session", { error });
    }
  });

  const createPortalMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data.url) {
        window.open(data.url, '_blank');
      }
    },
    onError: (error) => {
      logger.error("Failed to create customer portal session", { error });
    }
  });

  const refreshSubscriptionStatus = () => {
    queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
  };

  return {
    plans,
    plansLoading,
    subscriptionStatus,
    statusLoading,
    createCheckout: createCheckoutMutation.mutate,
    createCheckoutLoading: createCheckoutMutation.isPending,
    createPortal: createPortalMutation.mutate,
    createPortalLoading: createPortalMutation.isPending,
    refreshSubscriptionStatus,
  };
}
